import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useCan } from '../../contexts/PermissionsContext'
import { apiErrorDetail } from '../../lib/apiError'
import { useFormatters } from '../../lib/format'
import { SECTION_ICONS } from '../../lib/section-icons'
import { PageHeader } from '../shared/PageHeader'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Checkbox } from '../ui/checkbox'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { BookingDialog, useRefreshLetting, type Viewing } from './BookingDialog'

const NONE = '__none__'
const VIEWING_OUTCOMES = ['attended', 'no_show', 'interested', 'not_interested'] as const

/** The two badges of the agenda: advise not to go, and a pending notice. */
export function ViewingBadges({ viewing }: { viewing: Viewing }) {
    const { t } = useTranslation('viewings')
    return (
        <>
            {viewing.status === 'unconfirmed' && (
                <Badge variant="warning" data-testid="viewing-unconfirmed-badge">{t('badge.unconfirmed')}</Badge>
            )}
            {viewing.occupant_notice_pending && (
                <Badge variant="destructive" data-testid="viewing-occupant-not-notified-badge">{t('badge.occupant_not_notified')}</Badge>
            )}
        </>
    )
}

/** The local calendar day of a date, YYYY-MM-DD: the agenda is read in the viewer's time zone. */
export function dayKey(d: Date) {
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function startOf(date: string, view: string) {
    const d = new Date(date + 'T00:00:00')
    if (view === 'week') {
        const shift = (d.getDay() + 6) % 7
        d.setDate(d.getDate() - shift)
    }
    return d
}

function ViewingItem({ org, viewing, highlighted }: { org: string; viewing: Viewing; highlighted: boolean }) {
    const { t } = useTranslation('viewings')
    const { formatDate } = useFormatters()
    const canWrite = useCan('rental_applications.write')
    const refresh = useRefreshLetting()
    const [error, setError] = useState<string | null>(null)
    const patch = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/viewings/{viewingUid}', {
        onSuccess: (r) => {
            refresh()
            setError(r.notice_error ?? null)
        },
        onError: (err) => setError(apiErrorDetail(err, t('errors.generic'))),
    })
    const path = { orgCode: org, viewingUid: viewing.uid }
    return (
        <div
            className={`space-y-1 rounded-md border p-2 text-sm ${highlighted ? 'border-primary' : ''}`}
            data-testid="viewing-item"
            data-viewing-uid={viewing.uid}
            data-status={viewing.status}
        >
            <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{formatDate(viewing.starts_at, { timeStyle: 'short' })}–{formatDate(viewing.ends_at, { timeStyle: 'short' })}</span>
                <span>{viewing.asset_name}</span>
                <Badge variant="secondary" data-testid="viewing-item-status">{t(`status.${viewing.status}`)}</Badge>
                <span className="text-xs text-muted-foreground">{t(`kind.${viewing.kind}`)}</span>
                <ViewingBadges viewing={viewing} />
            </div>
            {viewing.host_name && <p className="text-xs text-muted-foreground">{t('item.host', { name: viewing.host_name })}</p>}
            {viewing.applications.map((a) => (
                <div key={a.uid} className="flex flex-wrap items-center gap-2 text-xs" data-testid="viewing-item-attendee" data-application-uid={a.application_uid} data-outcome={a.outcome ?? ''}>
                    <Link to="/$org/rental-applications/$applicationUid" params={{ org, applicationUid: a.application_uid }} className="underline" data-testid="viewing-item-application">
                        {a.lead_name ?? '—'}
                    </Link>
                    {canWrite ? (
                        <Select
                            value={a.outcome ?? NONE}
                            onValueChange={(v) => v !== NONE && patch.mutate({ params: { path }, body: { outcomes: { [a.application_uid]: v as 'attended' } } })}
                        >
                            <SelectTrigger className="h-7 w-40" data-testid="viewing-item-attendee-outcome-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value={NONE} disabled data-testid="viewing-item-attendee-outcome-none">{t('outcome.none')}</SelectItem>
                                {VIEWING_OUTCOMES.map((o) => (
                                    <SelectItem key={o} value={o} data-testid={`viewing-item-attendee-outcome-${o}`}>{t(`outcome.${o}`)}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        a.outcome && <Badge variant="outline" data-testid="viewing-item-attendee-outcome">{t(`outcome.${a.outcome}`)}</Badge>
                    )}
                </div>
            ))}
            {canWrite && (
                <div className="flex flex-wrap items-center gap-2">
                    <Select value={viewing.status} onValueChange={(v) => patch.mutate({ params: { path }, body: { status: v as 'completed' } })}>
                        <SelectTrigger className="h-8 w-44" data-testid="viewing-item-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {['proposed', 'scheduled', 'confirmed', 'unconfirmed', 'completed', 'no_show', 'cancelled'].map((s) => (
                                <SelectItem key={s} value={s} data-testid={`viewing-item-status-${s}`}>{t(`status.${s}`)}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {viewing.occupant_notice_pending && (
                        <>
                            <Button size="sm" variant="outline" onClick={() => patch.mutate({ params: { path }, body: { notify_occupant: true } })} data-testid="viewing-item-notify">
                                {t('item.notify')}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => patch.mutate({ params: { path }, body: { occupant_notified: true } })} data-testid="viewing-item-mark-notified">
                                {t('item.mark_notified')}
                            </Button>
                        </>
                    )}
                </div>
            )}
            {error && <p className="text-xs text-destructive" data-testid="viewing-item-error">{error}</p>}
        </div>
    )
}

function CalendarLinkCard({ org }: { org: string }) {
    const { t } = useTranslation('viewings')
    const { data, refetch } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/viewings/calendar', { params: { path: { orgCode: org } } })
    const rotate = $api.useMutation('post', '/api/v1/organizations/{orgCode}/viewings/calendar', { onSuccess: () => refetch() })
    return (
        <Card data-testid="viewings-calendar-card">
            <CardHeader>
                <CardTitle className="text-base">{t('calendar.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
                <p className="text-muted-foreground">{t('calendar.description')}</p>
                <Input readOnly value={data?.ics_url ?? ''} data-testid="viewings-calendar-link" onFocus={(e) => e.target.select()} />
                <div className="flex gap-2">
                    {data?.webcal_url && <a href={data.webcal_url} className="underline" data-testid="viewings-calendar-subscribe">{t('calendar.subscribe')}</a>}
                    <Button size="sm" variant="outline" onClick={() => rotate.mutate({ params: { path: { orgCode: org } } })} disabled={rotate.isPending} data-testid="viewings-calendar-rotate">
                        {t('calendar.rotate')}
                    </Button>
                </div>
            </CardContent>
        </Card>
    )
}

function SuggestionsCard({ org, assetUid, date }: { org: string; assetUid: string; date: string }) {
    const { t } = useTranslation('viewings')
    const [radius, setRadius] = useState('2')
    const { data } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/viewings/suggestions',
        { params: { path: { orgCode: org }, query: { asset_uid: assetUid, date, radius_km: Number(radius) } } },
        { enabled: !!assetUid },
    )
    return (
        <Card data-testid="viewing-suggestions">
            <CardHeader className="flex flex-row items-center justify-between gap-2">
                <CardTitle className="text-base">{t('suggestions.title')}</CardTitle>
                <Select value={radius} onValueChange={setRadius}>
                    <SelectTrigger className="h-8 w-28" data-testid="viewing-suggestions-radius-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        {['1', '2', '5', '10'].map((r) => <SelectItem key={r} value={r} data-testid={`viewing-suggestions-radius-${r}`}>{t('suggestions.radius', { count: Number(r) })}</SelectItem>)}
                    </SelectContent>
                </Select>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
                {!assetUid && <p className="text-muted-foreground">{t('suggestions.pick')}</p>}
                {data && (
                    <>
                        <p className="font-medium">{t('suggestions.same_day')}</p>
                        {data.viewings.length === 0 && <p className="text-muted-foreground">{t('suggestions.no_viewing')}</p>}
                        {data.viewings.map((v) => <p key={v.uid} data-testid="viewing-suggestion-viewing">{v.asset_name} · {new Date(v.starts_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>)}
                        <p className="font-medium">{t('suggestions.nearby')}</p>
                        {data.applications.length === 0 && <p className="text-muted-foreground">{t('suggestions.no_application')}</p>}
                        {data.applications.map((a) => (
                            <Link key={a.application_uid} to="/$org/rental-applications/$applicationUid" params={{ org, applicationUid: a.application_uid }} className="block underline" data-testid="viewing-suggestion-application">
                                {a.lead_name ?? '—'} · {a.asset_name} · {t('suggestions.km', { km: a.distance_km })}
                            </Link>
                        ))}
                    </>
                )}
            </CardContent>
        </Card>
    )
}

/**
 * The viewings agenda (spec 2026-10-25-12): by day or week, the booking of a
 * listing's viewing, the tour suggestions and the caller's ICS feed.
 */
export function ViewingsAgendaPage() {
    const { org } = useParams({ from: '/$org/rental-applications/viewings' })
    const search = useSearch({ from: '/$org/rental-applications/viewings' })
    const navigate = useNavigate()
    const { t } = useTranslation('viewings')
    const { formatDate } = useFormatters()
    const canWrite = useCan('rental_applications.write')
    const view = search.view ?? 'week'
    const date = search.date ?? dayKey(new Date())
    const [listingUid, setListingUid] = useState(NONE)
    const [picked, setPicked] = useState<string[]>([])
    const [booking, setBooking] = useState(false)
    useBreadcrumbs([
        { title: t('common:nav.home'), path: `/${org}` },
        { title: t('rentalApplications:title'), path: `/${org}/rental-applications` },
        { title: t('title') },
    ])
    const from = startOf(date, view)
    const to = new Date(from)
    to.setDate(to.getDate() + (view === 'week' ? 7 : 1))
    const { data } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/viewings', {
        params: { path: { orgCode: org }, query: { from: from.toISOString(), to: to.toISOString() } },
    })
    const { data: listings } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/listings', { params: { path: { orgCode: org } } })
    const { data: apps } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/rental-applications',
        { params: { path: { orgCode: org }, query: { listing_uid: listingUid, outcome: 'none' } } },
        { enabled: listingUid !== NONE },
    )
    const listing = (listings?.listings ?? []).find((l) => l.uid === listingUid)
    const days = useMemo(() => {
        const out: { key: string; items: Viewing[] }[] = []
        for (let d = new Date(from); d < to; d.setDate(d.getDate() + 1)) {
            const key = dayKey(d)
            out.push({ key, items: (data?.viewings ?? []).filter((v) => dayKey(new Date(v.starts_at)) === key) })
        }
        return out
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, from.getTime(), to.getTime()])
    const go = (next: Partial<{ view: 'day' | 'week'; date: string }>) =>
        navigate({ to: '/$org/rental-applications/viewings', params: { org }, search: { ...search, ...next }, replace: true })
    const shift = (n: number) => {
        const d = new Date(date + 'T00:00:00')
        d.setDate(d.getDate() + n * (view === 'week' ? 7 : 1))
        go({ date: dayKey(d) })
    }

    return (
        <div className="space-y-6" data-testid="viewings-page">
            <PageHeader icon={SECTION_ICONS['rental-applications']} title={t('title')} description={t('description')} />
            <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => shift(-1)} data-testid="viewings-agenda-previous">{t('agenda.previous')}</Button>
                <Input type="date" className="w-44" value={date} onChange={(e) => e.target.value && go({ date: e.target.value })} data-testid="viewings-agenda-date" />
                <Button size="sm" variant="outline" onClick={() => shift(1)} data-testid="viewings-agenda-next">{t('agenda.next')}</Button>
                <Select value={view} onValueChange={(v) => go({ view: v === 'day' ? 'day' : 'week' })}>
                    <SelectTrigger className="h-9 w-32" data-testid="viewings-agenda-view"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="day" data-testid="viewings-agenda-view-day">{t('agenda.day')}</SelectItem>
                        <SelectItem value="week" data-testid="viewings-agenda-view-week">{t('agenda.week')}</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
                <div className="space-y-3" data-testid="viewings-agenda">
                    {days.map((d) => (
                        <div key={d.key} data-testid="viewings-agenda-day" data-day={d.key}>
                            <p className="mb-1 text-sm font-medium">{formatDate(d.key + 'T12:00:00', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                            {d.items.length === 0 ? (
                                <p className="text-xs text-muted-foreground">{t('agenda.empty')}</p>
                            ) : (
                                <div className="space-y-2">
                                    {d.items.map((v) => <ViewingItem key={v.uid} org={org} viewing={v} highlighted={search.viewing === v.uid} />)}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                <div className="space-y-6">
                    {canWrite && (
                        <Card data-testid="viewings-book-card">
                            <CardHeader><CardTitle className="text-base">{t('booking.title')}</CardTitle></CardHeader>
                            <CardContent className="space-y-3 text-sm">
                                <div>
                                    <Label className="mb-1 block text-sm">{t('booking.listing')}</Label>
                                    <Select value={listingUid} onValueChange={(v) => { setListingUid(v); setPicked([]) }}>
                                        <SelectTrigger data-testid="viewings-book-listing"><SelectValue placeholder={t('booking.pick_listing')} /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value={NONE}>{t('booking.pick_listing')}</SelectItem>
                                            {(listings?.listings ?? []).filter((l) => l.status === 'published' || l.status === 'paused').map((l) => (
                                                <SelectItem key={l.uid} value={l.uid} data-testid={`viewings-book-listing-${l.uid}`}>{l.asset_name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                {(apps?.applications ?? []).filter((a) => !['offer_accepted', 'leased'].includes(a.stage)).map((a) => (
                                    <label key={a.uid} className="flex items-center gap-2">
                                        <Checkbox
                                            checked={picked.includes(a.uid)}
                                            onCheckedChange={(v) => setPicked(v === true ? [...picked, a.uid] : picked.filter((x) => x !== a.uid))}
                                            data-testid="viewings-book-application"
                                        />
                                        {a.lead_name ?? '—'} · {t(`rentalApplications:stage.${a.stage}`)}
                                    </label>
                                ))}
                                <Button size="sm" disabled={!listing} onClick={() => setBooking(true)} data-testid="viewings-book-button">{t('booking.open')}</Button>
                            </CardContent>
                        </Card>
                    )}
                    <SuggestionsCard org={org} assetUid={listing?.asset_uid ?? ''} date={date} />
                    <CalendarLinkCard org={org} />
                </div>
            </div>
            {listing && (
                <BookingDialog org={org} open={booking} onOpenChange={setBooking} listingUid={listing.uid} assetUid={listing.asset_uid} applicationUids={picked} />
            )}
        </div>
    )
}
